import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import SelectableDataGrid from '../../../../components/selectableDataGrid/SelectableDataGrid.component'
import { IonItemDivider, IonLabel } from '@ionic/react'
import {
  getImportSelectionsSelectedLabs,
  getImportSelectionsSelectedVitals,
  getPulsaraDetailsVitals,
  getPulsaraDetailsLabs,
} from '../../selectors'
import { setSelectedLabs, setSelectedVitals } from '../../store/pulsara.actions'
import { IPulsaraVitalsState, IPulsaraLabsState } from '../../store/pulsara.types'
import './PulsaraImport.component.css'
import { convertISOToHourAndMinute } from '../../../../utilities/ekgImport.util'

const formatTime = (row: Record<string, unknown>): string => {
  return row.dateTime ? convertISOToHourAndMinute(row.dateTime as string) : ''
}

const LABS_COLUMNS = [
  { key: 'time', label: 'Time', formatter: formatTime },
  { key: 'GLU', label: 'Glucose' },
  { key: 'HGB', label: 'Hemoglobin' },
  { key: 'PLTS', label: 'Platelets' },
  { key: 'INR', label: 'INR' },
  { key: 'CR', label: 'Creatinine' },
]

const VitalsLabs: React.FC = () => {
  const { t } = useTranslation()
  const pulsaraImportedVitals = useSelector(getPulsaraDetailsVitals)
  const pulsaraImportedLabs = useSelector(getPulsaraDetailsLabs)
  const importSelectionsSelectedVitals = useSelector(getImportSelectionsSelectedVitals)
  const importSelectionsSelectedLabs = useSelector(getImportSelectionsSelectedLabs)
  const dispatch = useDispatch()

  const hasVitals = pulsaraImportedVitals != null && pulsaraImportedVitals.length > 0
  const hasLabs = pulsaraImportedLabs != null && pulsaraImportedLabs.length > 0

  const onToggleItem = (checked: boolean, item: IPulsaraVitalsState | IPulsaraLabsState, type: 'vitals' | 'labs') => {
    const selected = type === 'vitals' ? importSelectionsSelectedVitals : importSelectionsSelectedLabs
    let dataToImport = selected ? [...selected] : []
    if (checked) {
      dataToImport.push(item)
    } else {
      dataToImport = dataToImport.filter((data: IPulsaraVitalsState | IPulsaraLabsState) => data.key !== item.key)
    }
    if (type === 'vitals') {
      dispatch(setSelectedVitals(dataToImport))
    } else {
      dispatch(setSelectedLabs(dataToImport))
    }
  }

  const onToggleAll = (checked: boolean, type: 'vitals' | 'labs') => {
    if (checked) {
      if (type === 'vitals') {
        dispatch(setSelectedVitals(pulsaraImportedVitals))
      } else {
        dispatch(setSelectedLabs(pulsaraImportedLabs))
      }
    } else {
      if (type === 'vitals') {
        dispatch(setSelectedVitals([]))
      } else {
        dispatch(setSelectedLabs([]))
      }
    }
  }

  return (
    <>
      <IonItemDivider color='light' className='pulsara-import-divider'>
        <IonLabel className='grid-item-divider-label'>{t('pulsara.Vitals')}</IonLabel>
      </IonItemDivider>
      {hasVitals ? (
        <SelectableDataGrid
          data={pulsaraImportedVitals}
          onToggleItem={(checked, item) => onToggleItem(checked, item as IPulsaraVitalsState, 'vitals')}
          onToggleAll={(checked) => onToggleAll(checked, 'vitals')}
          selectedItems={importSelectionsSelectedVitals}
        />
      ) : (
        <div className='pulsara-import-empty'>
          <IonLabel color='medium'>{t('pulsara.NoVitalsAvailable')}</IonLabel>
        </div>
      )}
      <IonItemDivider color='light' className='pulsara-import-divider'>
        <IonLabel className='grid-item-divider-label'>{t('pulsara.Labs')}</IonLabel>
      </IonItemDivider>
      {hasLabs ? (
        <SelectableDataGrid
          columns={LABS_COLUMNS}
          data={pulsaraImportedLabs}
          onToggleItem={(checked, item) => onToggleItem(checked, item as IPulsaraLabsState, 'labs')}
          onToggleAll={(checked) => onToggleAll(checked, 'labs')}
          selectedItems={importSelectionsSelectedLabs}
        />
      ) : (
        <div className='pulsara-import-empty'>
          <IonLabel color='medium'>{t('pulsara.NoLabsAvailable')}</IonLabel>
        </div>
      )}
    </>
  )
}

export default VitalsLabs
